"use client";

import * as React from "react";
import { Sun, Moon, MonitorSmartphone, Globe, Check } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Separator } from "@/components/ui/separator";
import { useTheme } from "@/hooks/theme";
import { useLocale } from "@/hooks/locale";
import { ThemedView } from "../ThemedView";
import { cn } from "@/lib/utils";

const THEME_OPTIONS = [
    { value: "light", icon: Sun, label: "Light" },
    { value: "system", icon: MonitorSmartphone, label: "System" },
    { value: "dark", icon: Moon, label: "Dark" },
] as const;

const LOCALE_OPTIONS = [
    { value: "en", label: "EN", full: "English" },
    { value: "ar", label: "AR", full: "العربية" },
] as const;

const trackClass =
    "inline-flex items-center gap-1 rounded-full border border-border/40 bg-muted/40 p-1 backdrop-blur-md shadow-inner transition-all duration-200 dark:bg-muted/20";

const itemClass =
    "relative flex h-7 items-center justify-center rounded-full px-2.5 text-xs font-semibold text-muted-foreground transition-all duration-200 hover:text-foreground active:scale-95 data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm dark:data-[state=on]:bg-accent dark:data-[state=on]:text-accent-foreground";

const ThemeAndLocale = () => {
    const { source: themeSource, setThemeSource, colors } = useTheme();
    const { localeCode: locale, setLocale, t } = useLocale();

    const ActiveThemeIcon =
        THEME_OPTIONS.find((o) => o.value === themeSource)?.icon ??
        MonitorSmartphone;

    const activeLocaleLabel =
        LOCALE_OPTIONS.find((o) => o.value === locale)?.label ?? "EN";

    return (
        <ThemedView className="flex items-center gap-3">
            {/* Desktop: Glassmorphic Segmented Controls */}
            <div className="hidden items-center gap-2.5 md:flex">
                {/* Theme Switcher */}
                <ToggleGroup
                    type="single"
                    value={themeSource}
                    onValueChange={(v) => v && setThemeSource(v as any)}
                    className={trackClass}
                >
                    {THEME_OPTIONS.map(({ value, icon: Icon, label }) => (
                        <ToggleGroupItem
                            key={value}
                            value={value}
                            aria-label={`${label} theme`}
                            className={cn(itemClass, "w-8 px-0")}
                        >
                            <Icon
                                className="h-3.5 w-3.5 transition-transform duration-200 group-hover:scale-110"
                                strokeWidth={2.2}
                            />
                        </ToggleGroupItem>
                    ))}
                </ToggleGroup>

                <Separator
                    orientation="vertical"
                    className="h-4 w-[1px] bg-border/40"
                />

                {/* Locale Switcher */}
                <ToggleGroup
                    type="single"
                    value={locale}
                    onValueChange={(v) => v && setLocale(v as any)}
                    className={trackClass}
                >
                    {LOCALE_OPTIONS.map(({ value, label, full }) => (
                        <ToggleGroupItem
                            key={value}
                            value={value}
                            aria-label={`Switch to ${full}`}
                            className={cn(itemClass, "min-w-[32px]")}
                        >
                            {label}
                        </ToggleGroupItem>
                    ))}
                </ToggleGroup>
            </div>

            {/* Mobile: Compact Quick-Menu Trigger */}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        aria-label="Theme and language settings"
                        className="flex h-9 items-center gap-2 rounded-full border border-border/40 bg-muted/40 px-3 text-xs font-medium backdrop-blur-md transition-all hover:bg-muted/80 active:scale-95 md:hidden"
                    >
                        <ActiveThemeIcon
                            className="h-3.5 w-3.5 text-foreground"
                            strokeWidth={2.2}
                        />
                        <Separator
                            orientation="vertical"
                            className="h-3.5 bg-border/50"
                        />
                        <span className="text-muted-foreground uppercase">
                            {activeLocaleLabel}
                        </span>
                    </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                    align="end"
                    sideOffset={8}
                    className="w-48 rounded-2xl p-1.5 backdrop-blur-lg"
                >
                    <ThemedView
                        style={{
                            padding: "12px",
                            gap: "22px",
                            color: colors.onSuccess,
                            background: colors.surface,
                        }}
                    >
                        <ThemedView
                            style={{
                                gap: "12px",
                            }}
                        >
                            {/* Theme Section */}
                            <DropdownMenuLabel
                                className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider"
                                style={{
                                    color: colors.onSurface,
                                }}
                            >
                                Theme
                            </DropdownMenuLabel>
                            {THEME_OPTIONS.map(
                                ({ value, icon: Icon, label }) => {
                                    const isSelected = themeSource === value;
                                    return (
                                        <DropdownMenuItem
                                            key={value}
                                            onClick={() =>
                                                setThemeSource(value as any)
                                            }
                                            className="flex items-center justify-between rounded-xl px-2.5 py-1.5 text-xs font-medium cursor-pointer"
                                            style={{
                                                color: colors.onSurface,
                                            }}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Icon
                                                    className="h-3.5 w-3.5 text-muted-foreground"
                                                    strokeWidth={2}
                                                />
                                                <span>{t(label)}</span>
                                            </div>
                                            {isSelected && (
                                                <Check className="h-3.5 w-3.5 text-primary" />
                                            )}
                                        </DropdownMenuItem>
                                    );
                                },
                            )}
                        </ThemedView>

                        <ThemedView
                            style={{
                                gap: "12px",
                            }}
                        >
                            {/* Language Section */}
                            <DropdownMenuLabel
                                className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
                                style={{
                                    color: colors.onSurface,
                                }}
                            >
                                {t("Language")}
                            </DropdownMenuLabel>
                            {LOCALE_OPTIONS.map(({ value, label, full }) => {
                                const isSelected = locale === value;
                                return (
                                    <DropdownMenuItem
                                        key={value}
                                        onClick={() => setLocale(value as any)}
                                        className="flex items-center justify-between rounded-xl px-2.5 py-1.5 text-xs font-medium cursor-pointer"
                                        style={{
                                            color: colors.onSurface,
                                        }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="w-5 text-center font-mono text-[10px] font-bold text-muted-foreground">
                                                {label}
                                            </span>
                                            <span>{full}</span>
                                        </div>
                                        {isSelected && (
                                            <Check className="h-3.5 w-3.5 text-primary" />
                                        )}
                                    </DropdownMenuItem>
                                );
                            })}
                        </ThemedView>
                    </ThemedView>
                </DropdownMenuContent>
            </DropdownMenu>
        </ThemedView>
    );
};

export default ThemeAndLocale;
